import { normalizeItineraryResourceNumericText } from "../../../shared/itinerary-resource-entry";
import type { QuoteBudgetLine } from "../types";
import {
  calculateBusBaseFeeAmount,
  calculateLineAmount,
  normalizeMoney,
  normalizeMoneyPlain,
  parseBusDailyFixedFormula,
  parseBusFuelFormula,
} from "./quoteBudgetCalculationService";

export {
  buildBusFuelFormula,
  busFuelRegionFromBasis,
  parseBusFuelRows,
  type BusFuelRegion,
  type BusFuelRow,
} from "./quoteBudgetCalculationService";

/**
 * 编辑器纯展示态服务：批量折叠、只看当前类、操作日志、行内公式轻编辑。
 * 全部为纯函数，不触碰人数草稿（对应 06-原型单元审计.md §4）。
 */

/** 折叠态映射：资源类型 -> 是否收起。 */
export type CollapseState = Record<string, boolean>;

const VERSION_STATUS_TEXT: Record<string, string> = {
  draft: "草稿",
  finalized: "已定稿",
  contract: "合同版本",
  archived: "已归档",
};

/** 页头与版本列表使用相同状态文案，未知状态保留原值。 */
export function versionStatusText(status: string): string {
  return VERSION_STATUS_TEXT[status] ?? status;
}

/**
 * 一键展开/收起全部（QB-PU-001）：有任一展开则全收起，否则全展开。
 * @param collapsed 当前折叠态
 * @param keys 参与折叠的资源类型集合
 */
export function toggleAllCollapsed(
  collapsed: CollapseState,
  keys: string[],
): { collapsed: CollapseState; label: string } {
  const allCollapsed = keys.every((key) => collapsed[key]);
  const nextCollapsed = !allCollapsed;
  const next: CollapseState = {};
  for (const key of keys) {
    next[key] = nextCollapsed;
  }
  // 按钮提示下一步动作：已全收起则提示展开，反之提示收起.
  return { collapsed: next, label: nextCollapsed ? "展开全部" : "收起全部" };
}

/**
 * 只看当前类过滤（QB-PU-010）：仅改变展示，不修改入参、不影响汇总全集。
 * @param lines 全部明细
 * @param options 过滤口径
 */
export function filterVisibleLines(
  lines: QuoteBudgetLine[],
  options: { onlyCurrent: boolean; activeType: string },
): QuoteBudgetLine[] {
  if (!options.onlyCurrent) {
    return [...lines];
  }
  return lines.filter((line) => line.resourceType === options.activeType);
}

/** 当前过滤后的可见条数（QB-PU-024）。 */
export function visibleLineCount(visibleLines: QuoteBudgetLine[]): number {
  return visibleLines.length;
}

/** 单条操作日志。 */
export type OperationLogEntry = { time: string; message: string };

/** 本地操作日志保留上限，避免无限增长。 */
const OPERATION_LOG_LIMIT = 50;

/**
 * 追加操作日志（QB-PU-023）：最新在前，超出上限丢弃最旧。
 * @param logs 现有日志
 * @param entry 新日志
 */
export function prependOperationLog(
  logs: OperationLogEntry[],
  entry: OperationLogEntry,
): OperationLogEntry[] {
  return [entry, ...logs].slice(0, OPERATION_LOG_LIMIT);
}

/** 从文本中提取全部数字。 */
function parseFormulaNumberTexts(text: string): string[] {
  return Array.from(String(text).matchAll(/(\d+(?:\.\d+)?)/g)).map((match) => match[1]);
}

/** 精确相乘非负十进制文本，并返回无多余零的十进制结果。 */
function multiplyDecimalTexts(values: readonly string[]): string | null {
  if (values.length === 0) return null;
  let coefficient = 1n;
  let scale = 0;
  for (const value of values) {
    const matched = value.match(/^(\d+)(?:\.(\d+))?$/);
    if (!matched) return null;
    const fraction = matched[2] ?? "";
    coefficient *= BigInt(`${matched[1]}${fraction}`);
    scale += fraction.length;
  }
  if (coefficient === 0n) return "0";
  while (scale > 0 && coefficient % 10n === 0n) {
    coefficient /= 10n;
    scale -= 1;
  }
  const digits = String(coefficient).padStart(scale + 1, "0");
  return scale === 0
    ? digits
    : `${digits.slice(0, -scale)}.${digits.slice(-scale)}`;
}

const MAX_SAFE_DECIMAL_2_TEXT = "90071992547409";

/** 是否为严格大于零的非负十进制文本。 */
function isPositiveDecimalText(value: string): boolean {
  return /[1-9]/.test(value);
}

/** 精确判断非负十进制文本是否超过前端安全金额边界。 */
function exceedsSafeDecimalLimit(value: string): boolean {
  const matched = value.match(/^(\d+)(?:\.(\d+))?$/);
  if (!matched) return true;
  const integer = matched[1].replace(/^0+(?=\d)/, "");
  if (integer.length !== MAX_SAFE_DECIMAL_2_TEXT.length) {
    return integer.length > MAX_SAFE_DECIMAL_2_TEXT.length;
  }
  if (integer !== MAX_SAFE_DECIMAL_2_TEXT) return integer > MAX_SAFE_DECIMAL_2_TEXT;
  return /[1-9]/.test(matched[2] ?? "");
}

/** 按十进制文本校验通用公式的数量、单价和乘积。 */
function decimalFormulaValidationMessage(quantity: string, unitPrice: string): string | null {
  if (!isPositiveDecimalText(quantity)) return "数量必须大于 0";
  const amount = multiplyDecimalTexts([quantity, unitPrice]);
  if (
    amount === null
    || exceedsSafeDecimalLimit(quantity)
    || exceedsSafeDecimalLimit(unitPrice)
    || exceedsSafeDecimalLimit(amount)
  ) return "金额或数量超出可安全计算范围";
  return null;
}

/** 把已提取的数字令牌转换为校验与数量运算使用的有限数值。 */
function parseFormulaNumbers(text: string): number[] {
  return parseFormulaNumberTexts(text).map((value) => Number(value));
}

const MAX_SAFE_DECIMAL_2 = Math.floor(Number.MAX_SAFE_INTEGER / 100);

/** 校验公式数量、单价和乘积都可按两位小数安全计算。 */
function formulaNumericValidationMessage(
  quantity: number,
  unitPrice: number,
  amount = quantity * unitPrice,
): string | null {
  if (quantity <= 0) return "数量必须大于 0";
  if (unitPrice < 0) return "数量和金额不能为负数";
  if (
    !Number.isFinite(quantity)
    || !Number.isFinite(unitPrice)
    || !Number.isFinite(amount)
    || quantity > MAX_SAFE_DECIMAL_2
    || unitPrice > MAX_SAFE_DECIMAL_2
    || amount > MAX_SAFE_DECIMAL_2
  ) return "金额或数量超出可安全计算范围";
  return null;
}

const UNIT_AWARE_FORMULA = /^\s*(\d+)\s*人\s*[xX×*]\s*(\d+)\s*(晚|天)\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*元\s*$/;
const BILLING_FACTOR_FORMULA = /^\s*(\d+(?:\.\d+)?)\s*(?:人)?\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*(餐|次)\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*(?:元)?\s*$/;
const BUS_VEHICLE_SEGMENT_FORMULA = /^\s*([1-9]\d*)\s*辆\s*[xX×*]\s*(\d+(?:\.\d+)?)\s*元\s*$/u;
const BUS_DAILY_DISPLAY_SEGMENT = "\\d+座[：:]\\s*\\d+(?:\\.\\d+)?车天\\s*x\\s*\\d+(?:\\.\\d+)?元\\s*x\\s*\\d+辆";
const BUS_DAILY_DISPLAY_FORMULA = new RegExp(`^\\s*${BUS_DAILY_DISPLAY_SEGMENT}(?:\\s*\\+\\s*${BUS_DAILY_DISPLAY_SEGMENT})*\\s*$`);
const TRANSPORT_FEE_FORMULA = /^\s*[1-9]\d*\s*(?:人)?\s*[xX×*]\s*[1-9]\d*\s*(?:次)?\s*[xX×*]\s*\d+(?:\.\d+)?\s*(?:元)?\s*$/;
const GENERIC_FORMULA = /^\s*\d+(?:\.\d+)?\s*(?:人次|人餐|人晚|人天|人项|车天|车趟|项|辆|张|人|晚|天|餐|次|元)?(?:\s*[xX×*]\s*\d+(?:\.\d+)?\s*(?:人次|人餐|人晚|人天|人项|车天|车趟|项|辆|张|人|晚|天|餐|次|元)?)*\s*$/;

/** 主表整段单车价口径：辆数 × 单车价；固定费行也允许，避免只能改原按天公式。 */
function parseBusVehicleSegmentFormula(text: string): { quantity: string; unitPrice: string; amount: string } | null {
  const matched = text.trim().match(BUS_VEHICLE_SEGMENT_FORMULA);
  if (!matched) return null;
  const quantity = matched[1]!;
  const unitPrice = normalizeMoneyPlain(matched[2]!) ?? matched[2]!;
  const amount = multiplyDecimalTexts([quantity, unitPrice]);
  if (amount === null) return null;
  return { quantity, unitPrice, amount };
}

/** 返回行内公式错误文案；null 表示可安全解析且没有未知片段。 */
export function inlineFormulaValidationMessage(line: QuoteBudgetLine, inputText: string): string | null {
  const text = normalizeItineraryResourceNumericText(inputText.trim());
  if (!text) return "请输入计算公式";
  if (/(?:^|[\sxX×*])[-−]\s*\d/.test(text)) return "数量和金额不能为负数";
  if (parseFormulaNumberTexts(text).some(exceedsSafeDecimalLimit)) {
    return "金额或数量超出可安全计算范围";
  }
  const busDailyFixed = parseBusDailyFixedFormula(text);
  if (busDailyFixed) {
    if (!isBusDailyFixedLine(line)) return "包车固定费公式仅适用于包车固定费明细";
    return formulaNumericValidationMessage(
      Number(busDailyFixed.quantity),
      Number(busDailyFixed.unitPrice),
      Number(busDailyFixed.amount),
    );
  }
  const busFuel = parseBusFuelFormula(text);
  if (busFuel) {
    if (line.resourceType !== "包车") return "包车燃油费公式仅适用于包车明细";
    if (isBusDailyFixedLine(line)) {
      return "包车燃油费公式仅适用于包车燃油费明细";
    }
    return formulaNumericValidationMessage(1, Number(busFuel.amount), Number(busFuel.amount));
  }
  const busVehicleSegment = parseBusVehicleSegmentFormula(text);
  if (busVehicleSegment) {
    if (!["包车", "大巴车"].includes(line.resourceType)) return "辆数×整段单车价公式仅适用于大巴明细";
    return decimalFormulaValidationMessage(busVehicleSegment.quantity, busVehicleSegment.unitPrice);
  }
  if (isBusDailyFixedLine(line)) {
    if (!BUS_DAILY_DISPLAY_FORMULA.test(text)) {
      return "公式格式不正确，请按当前车型公式输入";
    }
    const entries = busDailyFixedDisplayEntries(text);
    if (
      entries.length === 0
      || entries.some((entry) => !isPositiveDecimalText(entry.dayCount) || entry.count <= 0)
    ) {
      return "公式格式不正确，请按当前车型公式输入";
    }
    if (calculateBusBaseFeeAmount(entries) === null) {
      return "金额或数量超出可安全计算范围";
    }
    const structureMismatch = busDailyFixedDisplayStructureMismatch(line, entries);
    if (structureMismatch === "segments") {
      return "固定费展示公式仅允许修改单价，车型、车辆数以及分段数量和顺序必须保持不变";
    }
    if (structureMismatch === "dayCount") {
      return "公式中的车天必须与原计价天数一致";
    }
    const parsed = parseBusDailyFixedDisplayFormula(line, text);
    return parsed
      ? formulaNumericValidationMessage(
          Number(parsed.quantity),
          Number(parsed.budgetUnitPrice),
          Number(parsed.budgetAmount),
        )
      : "公式格式不正确，请按当前车型公式输入";
  }
  if (line.resourceType === "大交通" && line.resourceEntryType === "手续费") {
    if (!TRANSPORT_FEE_FORMULA.test(text)) return "手续费请按“人数 x 次数 x 单价”输入";
    const [people, times, unitPrice] = parseFormulaNumbers(text);
    return formulaNumericValidationMessage(people * times, unitPrice);
  }
  if (line.unit === "人晚" || line.unit === "人天") {
    const matched = text.match(UNIT_AWARE_FORMULA);
    const expectedDurationUnit = line.unit === "人晚" ? "晚" : "天";
    if (matched?.[3] !== expectedDurationUnit) {
      return `公式格式不正确，请按“人数 x ${expectedDurationUnit}数 x 单价”输入`;
    }
    return formulaNumericValidationMessage(Number(matched[1]) * Number(matched[2]), Number(matched[4]));
  }
  if (
    (line.resourceType === "考察点" || line.resourceType === "餐")
    && Number(line.manualPeopleCount) > 0
  ) {
    const matched = text.match(BILLING_FACTOR_FORMULA);
    const expectedBillingUnit = line.resourceType === "餐" ? "餐" : "次";
    if (matched?.[3] !== expectedBillingUnit) {
      return `公式格式不正确，请按“人数 x ${expectedBillingUnit}数 x 单价”输入`;
    }
    return formulaNumericValidationMessage(Number(matched[1]) * Number(matched[2]), Number(matched[4]));
  }
  if (!GENERIC_FORMULA.test(text)) return "公式格式不正确，请只输入数字、单位和乘号";
  const numberTexts = parseFormulaNumberTexts(text);
  const unitPrice = numberTexts.at(-1) ?? String(line.budgetUnitPrice);
  const quantity = numberTexts.length > 1
    ? multiplyDecimalTexts(numberTexts.slice(0, -1))
    : String(line.quantity);
  return quantity === null
    ? "金额或数量超出可安全计算范围"
    : decimalFormulaValidationMessage(quantity, unitPrice);
}

/**
 * 行内公式轻编辑（QB-PU-021）：解析数字写回数量/单价并重算金额。
 * 仅返回新行对象，绝不反写人数草稿（§4：正式版默认不静默反写人数）。
 * @param line 原预算行
 * @param inputText 公式输入文本，如 "46 x 3 x 120"
 */
export function applyInlineFormula(line: QuoteBudgetLine, inputText: string): QuoteBudgetLine {
  const normalizedInputText = normalizeItineraryResourceNumericText(inputText.trim());
  if (inlineFormulaValidationMessage(line, normalizedInputText)) return line;
  const busDailyFixed = parseBusDailyFixedFormula(normalizedInputText);
  if (busDailyFixed) {
    return {
      ...line,
      quantity: busDailyFixed.quantity,
      dayCount: busDailyFixed.dayCount,
      budgetUnitPrice: busDailyFixed.unitPrice,
      budgetAmount: busDailyFixed.amount,
      priceSourceType: "manual",
      priceBasisDesc: busDailyFixed.basis,
    };
  }
  const busDailyFixedDisplay = parseBusDailyFixedDisplayFormula(line, normalizedInputText);
  if (busDailyFixedDisplay) {
    return {
      ...line,
      ...busDailyFixedDisplay,
      priceSourceType: "manual",
    };
  }
  const busFuel = parseBusFuelFormula(normalizedInputText);
  if (busFuel) {
    return {
      ...line,
      quantity: "1",
      budgetUnitPrice: busFuel.amount,
      budgetAmount: busFuel.amount,
      priceSourceType: "manual",
      priceBasisDesc: busFuel.basis,
    };
  }
  const busVehicleSegment = parseBusVehicleSegmentFormula(normalizedInputText);
  if (busVehicleSegment && ["包车", "大巴车"].includes(line.resourceType)) {
    return {
      ...line,
      quantity: busVehicleSegment.quantity,
      budgetUnitPrice: busVehicleSegment.unitPrice,
      budgetAmount: busVehicleSegment.amount,
      priceSourceType: "manual",
    };
  }
  const unitAware = normalizedInputText.match(UNIT_AWARE_FORMULA);
  if (unitAware && ((line.unit === "人晚" && unitAware[3] === "晚") || (line.unit === "人天" && unitAware[3] === "天"))) {
    const people = Number(unitAware[1]);
    const duration = Number(unitAware[2]);
    const unitPrice = normalizeMoneyPlain(unitAware[4]) ?? String(line.budgetUnitPrice);
    const next: QuoteBudgetLine = {
      ...line,
      quantity: String(people * duration),
      manualPeopleCount: String(people),
      peopleMode: "manual",
      isLinkedToPeopleCount: false,
      budgetUnitPrice: unitPrice,
      priceSourceType: "manual",
      ...(unitAware[3] === "晚" ? { nightCount: duration } : { dayCount: duration }),
    };
    next.budgetAmount = calculateLineAmount(next);
    return next;
  }
  if ((line.unit === "人晚" || line.unit === "人天") && /人/.test(normalizedInputText) && /晚|天/.test(normalizedInputText)) {
    return line;
  }
  const numberTexts = parseFormulaNumberTexts(normalizedInputText);
  if (numberTexts.length === 0) {
    return line;
  }
  const unitPrice = normalizeMoneyPlain(numberTexts.at(-1) ?? String(line.budgetUnitPrice))
    ?? String(line.budgetUnitPrice);
  // 多个数字时，除单价外的数字相乘作为数量；单个数字只改单价.
  const quantity =
    numberTexts.length > 1
      ? multiplyDecimalTexts(numberTexts.slice(0, -1)) ?? String(line.quantity)
      : String(line.quantity);
  const next: QuoteBudgetLine = {
    ...line,
    quantity,
    budgetUnitPrice: String(unitPrice),
    priceSourceType: "manual",
    // 明确数量必须保存人工语义；单数字只调价格，保留原人数联动。
    ...(numberTexts.length > 1
      ? {
          peopleMode: "manual" as const,
          isLinkedToPeopleCount: false,
          ...(line.unit?.startsWith("人") ? { manualPeopleCount: quantity } : {}),
        }
      : {}),
    ...(line.resourceType === "大交通" && line.resourceEntryType === "手续费" && numberTexts.length >= 3
      ? {
          manualPeopleCount: numberTexts[0],
          itemSpecSnapshot: `${numberTexts[1]}次`,
        }
        : {}),
    ...((line.resourceType === "考察点" || line.resourceType === "餐") && numberTexts.length >= 3
      ? {
          manualPeopleCount: numberTexts[0],
        }
      : {}),
  };
  next.budgetAmount = calculateLineAmount(next);
  return next;
}

/**
 * 金额展示保留两位小数以内的真实精度，整数不强制补零。
 * @param value 金额，字符串或数字
 */
export function formatMoney(value: string | number): string {
  if (typeof value === "number" && !Number.isFinite(value)) return "¥0";
  const matched = String(value).trim().match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!matched) return "¥0";
  const fraction = matched[3] ?? "";
  let cents = BigInt(matched[2]) * 100n + BigInt(`${fraction}00`.slice(0, 2));
  if ((fraction[2] ?? "0") >= "5") cents += 1n;
  if (cents > 999_999_999_999_999_999n) return "¥0";
  const integerText = (cents / 100n).toLocaleString("zh-CN");
  const decimalText = String(cents % 100n).padStart(2, "0").replace(/0+$/, "");
  const sign = matched[1] === "-" && cents !== 0n ? "-" : "";
  return `¥${sign}${integerText}${decimalText ? `.${decimalText}` : ""}`;
}

/** 把后端锁定时间统一换算为北京时间；缺失和非法输入使用明确中文兜底。 */
export function formatCustomerQuoteLockedAt(value: string | null | undefined): string {
  if (!value) return "未记录";
  const naiveIsoDateTime = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;
  const parsed = new Date(naiveIsoDateTime.test(value) ? `${value}+08:00` : value);
  if (Number.isNaN(parsed.getTime())) return "时间格式异常";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(parsed);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")} ${part("hour")}:${part("minute")}:${part("second")}`;
}

/** 判断是否为考察点待核价行；人工确认 0 元后按真实金额展示。 */
export function isMissingPointUnitPrice(line: QuoteBudgetLine): boolean {
  return (
    line.resourceType === "考察点"
    && (line.priceSourceType ?? "unpriced") === "unpriced"
    && Number(line.budgetUnitPrice) === 0
  );
}

/** 定位问题明细时优先跳到待补价行；没有待补价时才回退到风险列表首行。 */
export function selectLocateRiskLine(riskLines: QuoteBudgetLine[]): QuoteBudgetLine | undefined {
  return riskLines.find((line) => line.priceSourceType === "unpriced" || isMissingPointUnitPrice(line))
    ?? riskLines[0];
}

/** 明细金额展示：V1 可显式显示自动缺价，人工版本保留既有口径。 */
export function formatLineAmountForDisplay(line: QuoteBudgetLine, includeAutoUnpriced = false): string {
  return isMissingPointUnitPrice(line) || ((includeAutoUnpriced || line.entrySnapshot) && line.priceSourceType === "unpriced") ? "待补价" : formatMoney(line.budgetAmount);
}

/** 把单行金额规整为整数分，供展示小计精确累加。 */
function lineMoneyCents(value: string | number): bigint {
  const matched = normalizeMoney(value).match(/^(-?)(\d+)\.(\d{2})$/);
  if (!matched) return 0n;
  const cents = BigInt(matched[2]) * 100n + BigInt(matched[3]);
  return matched[1] === "-" ? -cents : cents;
}

/** 把整数分还原为供金额展示函数消费的十进制文本。 */
function lineMoneyText(cents: bigint): string {
  const negative = cents < 0n;
  const absolute = negative ? -cents : cents;
  return `${negative ? "-" : ""}${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`;
}

/** 分组小计展示：V1 可显式包含自动缺价，避免未完整成本显示为零。 */
export function formatLineGroupTotalForDisplay(lines: QuoteBudgetLine[], includeAutoUnpriced = false): string {
  return lines.some((line) => isMissingPointUnitPrice(line) || ((includeAutoUnpriced || line.entrySnapshot) && line.priceSourceType === "unpriced"))
    ? "待补价"
    : formatMoney(lineMoneyText(lines.reduce(
        (sum, line) => sum + lineMoneyCents(line.budgetAmount),
        0n,
      )));
}

/** 只读原因码 -> 中文展示；保留 historical_version 仅兼容旧后端缓存。 */
const READONLY_REASON_TEXT: Record<string, string> = {
  initial_version: "初版预算只读，请复制为 V2 后编辑",
  historical_version: "历史版本，仅可查看",
  finalized: "已定稿，版本只读",
  customer_quote_finalized: "报价已定稿，当前核价版本不能修改",
  roster_pricing_locked: "人员名单已定稿，核价与报价暂不可修改或取消定稿；取消人员名单定稿后解除此限制。",
  contract: "已进入合同口径，版本只读",
};

/** 把后端只读原因码映射为中文；自定义/未登记文案原样透出，空值给通用兜底。 */
export function readonlyReasonText(reason: string | null | undefined): string {
  if (!reason) {
    return "当前版本只读";
  }
  return READONLY_REASON_TEXT[reason] ?? reason;
}

/** 判断写请求是否被已定稿对客报价的反向锁定阻断。 */
export function isCustomerQuoteLockError(error: unknown): boolean {
  return (error as { code?: unknown } | null)?.code
    === "QUOTE_BUDGET_LOCKED_BY_CUSTOMER_QUOTE";
}

/** 名单定价冻结独立于核价、报价自身的定稿状态。 */
export function isRosterPricingLockError(error: unknown): boolean {
  return (error as { code?: unknown } | null)?.code === "QUOTE_BUDGET_PRICING_FAMILY_LOCKED";
}

/** 定稿阻断具体原因码 -> 中文（后端 detail 透出时命中）。 */
const FINALIZE_BLOCK_REASON_TEXT: Record<string, string> = {
  student_count_required: "缺少学生人数",
  line_required: "缺少预算明细行",
  negative_amount: "存在负数金额",
  unpriced_line: "存在尚未核价的明细，请先录入价格；确认 0 元后再保存",
  zero_key_line_amount: "关键资源金额为 0，需确认风险",
};

/** 可由用户确认后强制通过的定稿风险码（对应后端 confirm_risk_codes）。 */
export const CONFIRMABLE_FINALIZE_RISK_CODES = ["zero_key_line_amount"] as const;

const ZERO_AMOUNT_KEY_RESOURCE_TYPES = ["住宿", "大交通", "包车", "考察点"] as const;

/** 汇总当前草稿中 0 元关键资源，帮助用户定位风险确认范围。 */
export function formatZeroKeyLineRiskSummary(lines: QuoteBudgetLine[]): string | null {
  const counts = new Map<string, number>();
  for (const line of lines) {
    if (
      !ZERO_AMOUNT_KEY_RESOURCE_TYPES.includes(
        line.resourceType as (typeof ZERO_AMOUNT_KEY_RESOURCE_TYPES)[number],
      )
      || Number(calculateLineAmount(line)) !== 0
    ) {
      continue;
    }
    counts.set(line.resourceType, (counts.get(line.resourceType) ?? 0) + 1);
  }
  const summary = ZERO_AMOUNT_KEY_RESOURCE_TYPES
    .flatMap((type) => counts.has(type) ? [`${type} ${counts.get(type)} 条`] : []);
  return summary.length > 0 ? summary.join("、") : null;
}

/** 定稿阻断展示文案：按后端 reason 给出可直接定位的中文提示。 */
export function finalizeBlockText(error: unknown): string | null {
  if (!error) {
    return null;
  }
  if (isItineraryChangedFinalizeError(error)) {
    return "当前行程已更新，本预算仍保留创建时的核价快照。请基于最新行程生成新预算版本后继续。";
  }
  const message = error instanceof Error ? error.message : String(error);
  if (Object.hasOwn(FINALIZE_BLOCK_REASON_TEXT, message)) {
    return `定稿被阻断：${FINALIZE_BLOCK_REASON_TEXT[message]}`;
  }
  if (message.startsWith("missing_resource_types:")) {
    return `定稿被阻断：缺少费用类型：${message.slice("missing_resource_types:".length)}`;
  }
  if (message.startsWith("missing_resource_lines:")) {
    return `定稿被阻断：缺少行程来源明细：${message.slice("missing_resource_lines:".length)}`;
  }
  if (message.startsWith("missing_bus_fixed_fee_lines:")) {
    return `定稿被阻断：缺少包车固定费：${message.slice("missing_bus_fixed_fee_lines:".length)}`;
  }
  const code = (error as { code?: unknown }).code;
  if (code === "QUOTE_BUDGET_FINALIZE_BLOCKED") {
    return "定稿被阻断：服务端未返回具体原因，请刷新后重试";
  }
  return `定稿被阻断：${message}`;
}

/** 是否为预算来源行程结构已变化错误。 */
export function isItineraryChangedFinalizeError(error: unknown): boolean {
  return (error as { code?: unknown } | null)?.code === "QUOTE_BUDGET_ITINERARY_CHANGED";
}

/** 是否为可确认的定稿风险（仅此类错误展示「确认风险并定稿」）。 */
export function isConfirmableFinalizeRisk(error: unknown): boolean {
  if (!error) {
    return false;
  }
  const message = error instanceof Error ? error.message : String(error);
  return CONFIRMABLE_FINALIZE_RISK_CODES.some((reason) => reason === message);
}

/** 去尾随零的纯数字串，用于公式中的人数/晚数/单价展示。 */
function plainNumber(value: string | number): string {
  const matched = String(value).trim().match(/^([+-]?)(\d+)(?:\.(\d+))?$/);
  if (!matched) return "0";
  const integer = matched[2].replace(/^0+(?=\d)/, "");
  const fraction = (matched[3] ?? "").replace(/0+$/, "");
  const isZero = integer === "0" && fraction === "";
  return `${matched[1] === "-" && !isZero ? "-" : ""}${integer}${fraction ? `.${fraction}` : ""}`;
}

type BusDailyFixedFormulaEntry = {
  seat: number;
  mappedSeat: number;
  month: string;
  price: string;
  count: number;
  dayCount: number | null;
};

type BusDailyFixedDisplayEntry = {
  seat: number;
  dayCount: string;
  price: string;
  count: number;
};

/** 解析固定费展示公式中的车型分段。 */
function busDailyFixedDisplayEntries(text: string): BusDailyFixedDisplayEntry[] {
  return Array.from(
    text.matchAll(/(\d+)座[：:]\s*(\d+(?:\.\d+)?)车天\s*x\s*(\d+(?:\.\d+)?)元\s*x\s*(\d+)辆/g),
  ).map((match) => ({
    seat: Number(match[1]),
    dayCount: match[2],
    price: match[3],
    count: Number(match[4]),
  }));
}

/** 是否为包车固定费行。 */
function isBusDailyFixedLine(line: QuoteBudgetLine): boolean {
  return line.resourceType === "包车" && line.resourceEntryType === "包车固定费";
}

/** 固定费行用车天数，优先使用后端/重排后的 dayCount。 */
function busDailyFixedDayCount(line: QuoteBudgetLine): number {
  const fromDayCount = Number(line.dayCount);
  if (Number.isFinite(fromDayCount) && fromDayCount > 0) return fromDayCount;
  const fromBasis = Number(String(line.priceBasisDesc ?? "").match(/用车天数=(\d+(?:\.\d+)?)/)?.[1]);
  if (Number.isFinite(fromBasis) && fromBasis > 0) return fromBasis;
  const fromQuantity = Number(line.quantity);
  return Number.isFinite(fromQuantity) && fromQuantity > 0 ? fromQuantity : 1;
}

/** 从固定费计价依据里提取车型、月份、每日基础费和车辆数。 */
function busDailyFixedEntries(line: QuoteBudgetLine): BusDailyFixedFormulaEntry[] {
  return Array.from(
    String(line.priceBasisDesc ?? "").matchAll(
      /(\d+)座(?:按(\d+)座)?\s*((?:1[0-2]|[1-9])月\s*)?(\d+(?:\.\d+)?)元\/天\s*x\s*(\d+)辆(?:\s*x\s*(\d+(?:\.\d+)?)天)?/g,
    ),
  ).map((match) => ({
    seat: Number(match[1]),
    mappedSeat: Number(match[2] ?? match[1]),
    month: (match[3] ?? "").trim(),
    price: match[4],
    count: Number(match[5]),
    dayCount: match[6] ? Number(match[6]) : null,
  }));
}

/** 展示公式仅允许改价，原计价分段必须同序且车型、车辆数和车天均不变。 */
function busDailyFixedDisplayStructureMismatch(
  line: QuoteBudgetLine,
  entries: readonly BusDailyFixedDisplayEntry[],
): "segments" | "dayCount" | null {
  const previous = busDailyFixedEntries(line);
  if (entries.length !== previous.length) return "segments";
  const defaultDayCount = busDailyFixedDayCount(line);
  for (const [index, entry] of entries.entries()) {
    const old = previous[index];
    if (!old || entry.seat !== old.seat || entry.count !== old.count) return "segments";
    if (plainNumber(entry.dayCount) !== plainNumber(old.dayCount ?? defaultDayCount)) {
      return "dayCount";
    }
  }
  return null;
}

/** 固定费展示公式按车型拆分，避免把不同车型合成一个车天单价。 */
function buildBusDailyFixedLineFormula(line: QuoteBudgetLine): string | null {
  const entries = busDailyFixedEntries(line);
  if (entries.length === 0) return null;
  const dayCount = busDailyFixedDayCount(line);
  const unit = line.unit || "车天";
  return entries
    .map((entry) => `${entry.seat}座：${plainNumber(entry.dayCount ?? dayCount)}${unit} x ${plainNumber(entry.price)}元 x ${entry.count}辆`)
    .join(" + ");
}

/** 解析按车型拆分后的固定费展示公式，支持用户直接确认或微调单价。 */
function parseBusDailyFixedDisplayFormula(
  line: QuoteBudgetLine,
  text: string,
): Pick<QuoteBudgetLine, "quantity" | "dayCount" | "budgetUnitPrice" | "budgetAmount" | "priceBasisDesc"> | null {
  if (!isBusDailyFixedLine(line)) return null;
  const entries = busDailyFixedDisplayEntries(text);
  if (
    !BUS_DAILY_DISPLAY_FORMULA.test(text)
    || entries.length === 0
    || entries.some((entry) => !isPositiveDecimalText(entry.dayCount) || entry.count <= 0)
    || busDailyFixedDisplayStructureMismatch(line, entries) !== null
  ) return null;
  const dayCount = busDailyFixedDayCount(line);
  const exactAmount = calculateBusBaseFeeAmount(entries);
  if (exactAmount === null) return null;
  const exactFixed = parseBusDailyFixedFormula(
    `包车固定费=${exactAmount}；用车天数=${plainNumber(dayCount)}`,
  );
  if (exactFixed === null) return null;
  const previous = busDailyFixedEntries(line);
  const details = entries.map((entry, index) => {
    const old = previous[index] ?? previous.find((item) => item.seat === entry.seat);
    const mapped = old && old.mappedSeat !== entry.seat ? `按${old.mappedSeat}座` : "";
    const month = old?.month ? `${old.month} ` : "";
    const entryDays = old?.dayCount ? ` x ${plainNumber(entry.dayCount)}天` : "";
    return `${entry.seat}座${mapped} ${month}${plainNumber(entry.price)}元/天 x ${entry.count}辆${entryDays}`;
  });
  const days = dayCount > 1 ? `用车天数=${plainNumber(dayCount)}；` : "";
  return {
    quantity: plainNumber(dayCount),
    dayCount,
    budgetUnitPrice: exactFixed.unitPrice,
    budgetAmount: exactAmount,
    priceBasisDesc: `按车型每日用车固定费：${days}${details.join("；")}`,
  };
}

/**
 * 行内公式展示文本（只读派生，对应原型 formulaText）。
 * 人晚行按 数量/晚数 还原人数，拆为 `人 x 晚 x 单价`；其余行按 `数量单位 x 单价`。
 * @param line 预算行
 */
export function buildLineFormula(line: QuoteBudgetLine): string {
  if (line.entrySnapshot?.inputMode === "manual_v2") {
    const snapshot = line.entrySnapshot;
    const price = plainNumber(line.budgetUnitPrice);
    if (snapshot.quantityMode === "total") return `${price}元`;
    if (snapshot.quantityMode === "quantity") return `${plainNumber(line.quantity)}${line.unit} x ${price}元`;
    const people = Number(line.quantity) / snapshot.billingCount;
    const factorUnit = line.unit === "人晚" ? "晚" : line.unit === "人餐" ? "餐" : line.unit === "人天" ? "天" : "次";
    return `${plainNumber(people)}人 x ${snapshot.billingCount}${factorUnit} x ${price}元`;
  }
  const busDailyFixedFormula = buildBusDailyFixedLineFormula(line);
  if (busDailyFixedFormula) {
    return busDailyFixedFormula;
  }
  const price = plainNumber(line.budgetUnitPrice);
  const quantity = Number(line.quantity);
  const transportFeeTimes = Number(line.itemSpecSnapshot?.match(/^(\d+)次$/)?.[1]);
  const manualTransportFeePeople = Number(line.manualPeopleCount);
  const transportFeePeople = line.isLinkedToPeopleCount
    ? quantity / transportFeeTimes
    : manualTransportFeePeople > 0
      ? manualTransportFeePeople
      : quantity / transportFeeTimes;
  if (
    line.resourceType === "大交通"
    && line.resourceEntryType === "手续费"
    && transportFeePeople > 0
    && transportFeeTimes > 0
  ) {
    return `${plainNumber(transportFeePeople)}人 x ${plainNumber(transportFeeTimes)}次 x ${price}元`;
  }
  const nights = line.nightCount ?? null;
  if (line.unit === "人晚" && nights && nights > 0) {
    const people = quantity / nights;
    return `${plainNumber(people)}人 x ${plainNumber(nights)}晚 x ${price}元`;
  }
  const days = line.dayCount ?? null;
  if (line.unit === "人天" && days && days > 0) {
    const people = quantity / days;
    return `${plainNumber(people)}人 x ${plainNumber(days)}天 x ${price}元`;
  }
  const manualPeople = Number(line.manualPeopleCount);
  if (
    (line.resourceType === "考察点" || line.resourceType === "餐")
    && manualPeople > 0
    && quantity > 0
  ) {
    const billingFactor = quantity / manualPeople;
    const billingUnit = line.resourceType === "餐" ? "餐" : "次";
    return `${plainNumber(manualPeople)}人 x ${plainNumber(billingFactor)}${billingUnit} x ${price}元`;
  }
  return `${plainNumber(line.quantity)}${line.unit || ""} x ${price}元`;
}

/** 明细公式展示：考察点缺价时展示待补价，编辑预填仍使用 buildLineFormula 的数值口径。 */
export function formatLineFormulaForDisplay(line: QuoteBudgetLine): string {
  if (line.entrySnapshot?.inputMode === "manual_v2" && line.priceSourceType === "unpriced") {
    return buildLineFormula(line).replace(/[\d.]+元$/, "待补价");
  }
  if (isMissingPointUnitPrice(line)) {
    return `${plainNumber(line.quantity)}${line.unit || ""} x 待补价`;
  }
  return buildLineFormula(line);
}
